import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, X } from 'lucide-react';
import { categoriesFallback } from '../data';

export default function CategoryDetail() {
  const { slug } = useParams();
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCatalogUrl, setActiveCatalogUrl] = useState(null);
  const [activeCatalogTitle, setActiveCatalogTitle] = useState('');

  useEffect(() => {
    fetch(`https://api.dev.auxo.dk/public/content/categories`)
      .then(res => res.json())
      .then(data => {
        let found = null;
        if (data && data.length > 0) {
          found = data.find(item => {
            const itemSlug = typeof item.slug === 'object' ? item.slug.da : item.slug;
            return itemSlug === slug;
          });
        }
        
        // Flet underkategorier fra fallback hvis de findes
        const fallbackMatch = categoriesFallback.find(item => item.slug.da === slug);
        if (found && fallbackMatch && fallbackMatch.subcategories) {
          found = {
            ...found,
            subcategories: found.subcategories || fallbackMatch.subcategories
          };
        }
        
        if (!found) {
          found = categoriesFallback.find(item => item.slug.da === slug);
        }
        
        setCategory(found);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        const found = categoriesFallback.find(item => item.slug.da === slug);
        setCategory(found);
        setLoading(false);
      });
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-none border-4 border-blue-600 border-t-transparent" />
      </div>
    );
  }

  if (!category) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6">
        <h1 className="text-4xl font-bold text-[#1e335e] uppercase">Kategorien blev ikke fundet</h1>
        <Link to="/" className="mt-6 text-blue-600 hover:underline">Gå tilbage til forsiden</Link>
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Breadcrumbs */}
      <nav className="mx-auto max-w-7xl px-6 py-8 lg:px-8" aria-label="Breadcrumb">
        <ol role="list" className="flex items-center space-x-4">
          <li>
            <div>
              <Link to="/" className="text-sm font-medium text-gray-500 hover:text-gray-700">
                Forside
              </Link>
            </div>
          </li>
          <li>
            <div className="flex items-center">
              <ChevronRight className="h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
              <span className="ml-4 text-sm font-medium text-gray-500">{category.title.da}</span>
            </div>
          </li>
        </ol>
      </nav>

      <main className="mx-auto max-w-7xl px-6 pb-24 lg:px-8">
        <div className="sm:grid sm:grid-cols-2 sm:gap-x-8 md:gap-x-12 lg:gap-x-16">
          {/* Image */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="h-[400px] sm:h-[550px] w-full overflow-hidden rounded-none bg-gray-100"
          >
            <img
              src={category.image[0]}
              alt={category.title.da}
              className="h-full w-full object-cover object-center"
            />
          </motion.div>

          {/* Content */}
          <div className="mt-10 md:mt-0">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-3xl font-bold tracking-tight text-[#1e335e] sm:text-5xl uppercase"
            >
              {category.title.da}
            </motion.h1>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mt-6 text-gray-600 text-lg leading-relaxed 
                [&_p]:mb-6 
                [&_ul]:list-none [&_ul]:mb-6 [&_ul]:mt-4
                [&_li]:mb-3 [&_li]:pl-8 [&_li]:relative
                [&_li::before]:content-['✓'] [&_li::before]:absolute [&_li::before]:left-0 [&_li::before]:text-black [&_li::before]:font-bold [&_li::before]:text-xl"
              dangerouslySetInnerHTML={{ __html: category.description.da }}
            />
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mt-10"
            >
              <Link to="/kontakt" className="inline-block w-full sm:w-auto text-center rounded-none bg-orange-500 px-8 py-4 text-sm font-semibold text-white hover:bg-orange-600 transition-all transform hover:-translate-y-1 hover:shadow-lg duration-200 uppercase">
                Kontakt os for tilbud
              </Link>
            </motion.div>
          </div>
        </div>

        {/* Subcategories Section */}
        {category.subcategories && category.subcategories.length > 0 && (
          <div className="mt-24 border-t border-gray-100 pt-16">
            <h2 className="text-2xl font-bold tracking-tight text-[#1e335e] sm:text-3xl uppercase mb-10">
              Udforsk vores sortiment inden for {category.title.da}
            </h2>
            <div className="grid grid-cols-1 gap-y-12 sm:grid-cols-2 sm:gap-x-8 lg:grid-cols-3 lg:gap-x-12">
              {category.subcategories.map((sub, idx) => (
                <motion.div
                  key={sub.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="group relative flex flex-col overflow-hidden border border-gray-100 bg-white p-6"
                >
                  <div className="aspect-h-1 aspect-w-1 overflow-hidden bg-gray-100 group-hover:opacity-90 transition-opacity">
                    <img
                      src={sub.image}
                      alt={sub.title}
                      className="h-72 w-full object-cover object-center sm:h-80"
                    />
                  </div>
                  <div className="flex flex-1 flex-col space-y-2 p-4 pt-6">
                    <h3 className="text-xl font-bold text-gray-900 uppercase">
                      {sub.title}
                    </h3>
                    <p className="text-sm text-gray-500 flex-1 leading-relaxed">
                      {sub.description}
                    </p>
                    <div className="pt-4">
                      {sub.links && sub.links.length > 0 ? (
                        <div className="flex flex-col space-y-2.5">
                          {sub.links.map((lnk) => (
                            <a
                              key={lnk.url}
                              href={lnk.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => {
                                if (lnk.url.includes('joomag.com')) {
                                  e.preventDefault();
                                  setActiveCatalogUrl(lnk.url);
                                  setActiveCatalogTitle(lnk.label);
                                }
                              }}
                              className="inline-flex items-center text-sm font-bold text-orange-500 hover:text-orange-600 transition-colors uppercase tracking-wider cursor-pointer"
                            >
                              {lnk.label}
                              <ChevronRight className="ml-1 h-4 w-4" />
                            </a>
                          ))}
                        </div>
                      ) : sub.link ? (
                        <a
                          href={sub.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => {
                            if (sub.link.includes('joomag.com')) {
                              e.preventDefault();
                              setActiveCatalogUrl(sub.link);
                              setActiveCatalogTitle(sub.title);
                            }
                          }}
                          className="inline-flex items-center text-sm font-bold text-orange-500 hover:text-orange-600 transition-colors uppercase tracking-wider cursor-pointer"
                        >
                          Se online katalog
                          <ChevronRight className="ml-1 h-4 w-4" />
                        </a>
                      ) : (
                        <Link
                          to="/kontakt"
                          className="inline-flex items-center text-sm font-bold text-orange-500 hover:text-orange-600 transition-colors uppercase tracking-wider"
                        >
                          Få et uforpligtende tilbud
                          <ChevronRight className="ml-1 h-4 w-4" />
                        </Link>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Catalog Modal */}
      <AnimatePresence>
        {activeCatalogUrl && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 sm:p-6 md:p-10 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              className="relative flex h-full w-full max-w-7xl flex-col bg-white shadow-2xl overflow-hidden"
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-6 py-4">
                <div className="flex flex-col">
                  <span className="text-xs font-semibold uppercase tracking-wider text-orange-500">HN Promotion Katalog</span>
                  <h3 className="text-lg font-bold text-[#1e335e] uppercase truncate max-w-[150px] sm:max-w-md md:max-w-xl">
                    {activeCatalogTitle}
                  </h3>
                </div>
                <div className="flex items-center gap-2 sm:gap-4">
                  <a
                    href={activeCatalogUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-none border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors uppercase tracking-wider"
                  >
                    Åbn i nyt vindue
                  </a>
                  <button
                    onClick={() => {
                      setActiveCatalogUrl(null);
                      setActiveCatalogTitle('');
                    }}
                    className="rounded-none p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors cursor-pointer"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {/* Iframe Content */}
              <div className="flex-1 bg-gray-100 relative">
                <iframe
                  src={activeCatalogUrl}
                  title={activeCatalogTitle}
                  className="absolute inset-0 h-full w-full border-0"
                  allowFullScreen
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
